import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Form,
  Input,
  Typography,
  Select,
  Checkbox,
  Switch,
  Button,
} from 'antd';
// Api
import {
  CREATE_CAMPAIGN_EXAMPLE_FILES,
  UPDATE_SPONSORED_APPEARANCE_CAMPAIGN,
} from 'api/campaignV2/mutations';
import { SEARCH_SPONSORED_APPEARANCE_CAMPAIGN_STORES } from 'api/campaignV2/queries';
// Helpers
import { getSponsoredAppearanceActivity } from 'helpers/cbActivity';
import {
  FOLLOWING_OPTIONS_MAX,
  FOLLOWING_OPTIONS_MIN,
  PRODUCT_TYPES,
  uploadImages,
} from 'helpers/campaignEditModal';
// Types
import {
  GetCampaignsV2_getCampaigns_entities,
  GetCampaignsV2_getCampaigns_entities_activities_SponsoredAppearanceActivity_target,
  GetCampaignsV2_getCampaigns_entities_search,
  GetCampaignsV2_getCampaigns_entities_search_SponsoredAppearanceSearchCriteria,
} from 'api/campaignV2/types/GetCampaignsV2';
import {
  CampaignActivityTargetType,
  CampaignActivityType,
  SearchSponsoredAppearanceCriteriaInput,
  SponsoredAppearanceActivityDetailsInput,
  SponsoredAppearanceActivityInput,
  SponsoredAppearanceVideoType,
  StoreGender,
  UpdateSponsoredAppearanceInput,
  UserRole,
  CbApplicationQuestionType,
  AudienceAgeRange,
  AudienceEthnicity,
  AudienceGender,
  AudienceInterest,
  CampaignLocationInput,
} from 'api/graphql-global-types';
import {
  CreateCampaignExampleFiles,
  CreateCampaignExampleFilesVariables,
} from 'api/campaignV2/types/CreateCampaignExampleFiles';
import {
  UpdateSponsoredAppearanceCampaign,
  UpdateSponsoredAppearanceCampaignVariables,
} from 'api/campaignV2/types/UpdateSponsoredAppearanceCampaign';
import {
  SearchSponsoredAppearanceCampaignStores,
  SearchSponsoredAppearanceCampaignStoresVariables,
} from 'api/campaignV2/types/SearchSponsoredAppearanceCampaignStores';
import { buildAudienceFilters } from 'helpers/audienceFilters';
// Hooks
import { useGetMyEntitlements } from 'hooks';
// Helpers
import { canEditPremiumFilters } from 'helpers/brands';
// Ui
import UploadFilesInput from 'ui/UploadFIlesInput/UploadFilesInput';
import { errorNotification, successNotification } from 'ui/Notification';
// Components
import TalentQuestions from 'pages/CampaignDetails/CampaignDetails/components/TalentQuestions/TalentQuestions';
import CampaignCriteria from 'components/common/CampaignEditComponents/CampaignCriteria/CampaignCriteria';
import CampaignDetails from 'components/common/CampaignEditComponents/CampaignDetails/CampaignDetails';
// Style
import styles from './SponsoredAppearanceCampaignEditModal.module.scss';

const { Option } = Select;
const { Text } = Typography;
const { TextArea } = Input;

type FormValues = {
  // criteria/filters
  userRole: UserRole[];
  budget: number;
  flatFeePerStore: number;
  genders?: StoreGender[] | null;
  maxAge?: number | null;
  minAge?: number | null;
  followingFrom: number | null;
  followingTo: number | null;
  sports?: string[] | null;
  // sponsored appearance common
  appearanceVideoType?: SponsoredAppearanceVideoType | null;
  name: string;
  internalName: string;
  dueDate: any;
  applicationDeadlineDays?: number | null;
  applicationDeadlineEnabled?: boolean;
  // target (common)
  appearanceTargetType?: CampaignActivityTargetType | null;
  type?: CampaignActivityType | null;
  instagram?: string | null;
  instructions: string;
  linkedin?: string | null;
  otherSocial?: string | null;
  // target (guest only)
  guestName?: string | null;
  guestBio?: string | null;
  company?: string | null;
  email?: string | null;
  // target (product only)
  brandName?: string | null;
  productBio?: string | null;
  website?: string | null;
  physicalProductType?: string | null;
  productName?: string | null;
  productValue?: number | null;
  usageRightsText: string | null;
  questions: string[];
  exampleFiles?: any;
  audienceGender?: AudienceGender | null;
  audienceAgeRanges?: AudienceAgeRange[] | null;
  audienceEthnicities?: AudienceEthnicity[] | null;
  audienceLocations?: CampaignLocationInput[] | null;
  audienceInterests?: AudienceInterest[] | null;
};

type SponsoredAppearanceCampaignEditModalProps = {
  record: GetCampaignsV2_getCampaigns_entities;
  onFinish: () => void;
};

const normFile = (e: any) => {
  // AntD expects an array of files
  if (Array.isArray(e)) {
    return e;
  }
  return e && e.fileList;
};

const SponsoredAppearanceCampaignEditModal = ({
  record,
  onFinish,
}: SponsoredAppearanceCampaignEditModalProps): JSX.Element | null => {
  const [form] = Form.useForm();

  const ownerId = record?.agency?.id;
  const { data: entitlementsData } = useGetMyEntitlements({
    ownerId,
    skip: !ownerId,
  });
  const canEditPremiumFiltersValue = canEditPremiumFilters(
    entitlementsData?.getMyEntitlements
  );

  const questions = record?.questions
    ?.filter((item) => item.type === CbApplicationQuestionType.Regular)
    ?.sort((a, b) => a.order - b.order);

  const [isUploading, setIsUploading] = useState<boolean>(false);

  const sponsoredAppearanceActivity = getSponsoredAppearanceActivity(record);

  const [isLive, setIsLive] = useState<boolean>(
    sponsoredAppearanceActivity &&
      sponsoredAppearanceActivity.videoType ===
        SponsoredAppearanceVideoType.Live
  );

  const [usageRights, setUsageRights] = useState<boolean>(
    record?.usageRights || false
  );

  const [isAmplify, setIsAmplify] = useState<boolean>(
    sponsoredAppearanceActivity?.withAmplify || false
  );

  const target =
    sponsoredAppearanceActivity?.target as GetCampaignsV2_getCampaigns_entities_activities_SponsoredAppearanceActivity_target;

  const search =
    record?.search as GetCampaignsV2_getCampaigns_entities_search_SponsoredAppearanceSearchCriteria;

  const appearanceTargetTypeGuest =
    search?.appearanceTargetType === CampaignActivityTargetType.SponsoredGuest;

  const [isGuest, setIsGuest] = useState<boolean>(
    appearanceTargetTypeGuest || false
  );

  const initialProduct =
    target && 'physicalProduct' in target ? target.physicalProduct : false;

  const [isPhysicalProduct, setIsPhysicalProduct] =
    useState<boolean>(initialProduct);

  const [searchStores, { data: storeData, loading: loadingStoreData }] =
    useLazyQuery<
      SearchSponsoredAppearanceCampaignStores,
      SearchSponsoredAppearanceCampaignStoresVariables
    >(SEARCH_SPONSORED_APPEARANCE_CAMPAIGN_STORES);

  const [
    updateSponsoredAppearanceCampaign,
    { loading: loadingUpdateCampaign },
  ] = useMutation<
    UpdateSponsoredAppearanceCampaign,
    UpdateSponsoredAppearanceCampaignVariables
  >(UPDATE_SPONSORED_APPEARANCE_CAMPAIGN);

  const [
    createCampaignExampleFiles,
    { loading: loadingCreateCampaignExampleFiles },
  ] = useMutation<
    CreateCampaignExampleFiles,
    CreateCampaignExampleFilesVariables
  >(CREATE_CAMPAIGN_EXAMPLE_FILES);

  const filters = record?.search as GetCampaignsV2_getCampaigns_entities_search;

  const initialFormValues: FormValues = {
    userRole: filters?.roles || [],
    budget: filters?.budget || 0,
    flatFeePerStore: record?.flatFeePerStore || 0,
    genders: filters?.genders || [],
    maxAge: filters?.athleteMaxAge || null,
    minAge: filters?.athleteMinAge || null,
    followingFrom:
      FOLLOWING_OPTIONS_MIN.find((f) => f.value === filters.minFollowers)
        ?.value || null,
    followingTo:
      FOLLOWING_OPTIONS_MAX.find((f) => f.value === filters.maxFollowers)
        ?.value || null,
    sports: filters?.sports || [],
    appearanceVideoType: sponsoredAppearanceActivity?.videoType,
    name: record?.name || '',
    internalName: record?.internalName || '',
    dueDate: record?.dueDate ? moment(record?.dueDate) : null,
    applicationDeadlineDays: record?.applicationDeadlineDays || null,
    applicationDeadlineEnabled: record?.applicationDeadlineEnabled ?? true,
    type: sponsoredAppearanceActivity?.type,
    appearanceTargetType: target?.type,
    instagram: target?.instagram || '',
    instructions: target?.instructions || '',
    linkedin: target?.linkedin || '',
    otherSocial: target?.otherSocial || '',
    guestName: target && 'guestName' in target ? target.guestName : '',
    guestBio: target && 'guestBio' in target ? target.guestBio : '',
    company: target && 'company' in target ? target.company : '',
    email: target && 'email' in target ? target.email : '',
    brandName: target && 'brandName' in target ? target.brandName : '',
    productBio: target && 'productBio' in target ? target.productBio : '',
    website: target && 'website' in target ? target.website : '',
    physicalProductType:
      target && 'physicalProductType' in target
        ? target.physicalProductType
        : '',
    productName: target && 'productName' in target ? target.productName : null,
    productValue:
      target && 'productValue' in target ? target.productValue : null,
    usageRightsText: record?.usageRightsText || null,
    questions: questions?.map((item) => item.question),
    exampleFiles: target && 'files' in target ? target.files : [],
    audienceGender: filters?.audienceGender || null,
    audienceAgeRanges: filters?.audienceAgeRanges || [],
    audienceEthnicities: filters?.audienceEthnicities || [],
    audienceLocations: filters?.audienceLocations || [],
    audienceInterests: filters?.audienceInterests || [],
  };

  // Set initial form values when record changes (only reset when editing a different campaign)
  useEffect(() => {
    if (record && filters) {
      form.setFieldsValue({
        name: record?.name || '',
        internalName: record?.internalName || '',
        dueDate: record?.dueDate ? moment(record?.dueDate) : null,
        applicationDeadlineDays: record?.applicationDeadlineDays || null,
        applicationDeadlineEnabled: record?.applicationDeadlineEnabled ?? true,
        appearanceVideoType: sponsoredAppearanceActivity?.videoType,
        type: sponsoredAppearanceActivity?.type,
        appearanceTargetType: target?.type,
        instagram: target?.instagram || '',
        instructions: target?.instructions || '',
        linkedin: target?.linkedin || '',
        otherSocial: target?.otherSocial || '',
        guestName: target && 'guestName' in target ? target.guestName : '',
        guestBio: target && 'guestBio' in target ? target.guestBio : '',
        company: target && 'company' in target ? target.company : '',
        email: target && 'email' in target ? target.email : '',
        brandName: target && 'brandName' in target ? target.brandName : '',
        productBio: target && 'productBio' in target ? target.productBio : '',
        website: target && 'website' in target ? target.website : '',
        physicalProductType:
          target && 'physicalProductType' in target
            ? target.physicalProductType
            : '',
        productName:
          target && 'productName' in target ? target.productName : null,
        productValue:
          target && 'productValue' in target ? target.productValue : null,
        usageRightsText: record?.usageRightsText || null,
        exampleFiles: target && 'files' in target ? target.files : [],
        userRole: filters?.roles || [],
        budget: filters?.budget || 0,
        flatFeePerStore: record?.flatFeePerStore || 0,
        genders: filters?.genders || [],
        maxAge: filters?.athleteMaxAge || null,
        minAge: filters?.athleteMinAge || null,
        followingFrom:
          FOLLOWING_OPTIONS_MIN.find((f) => f.value === filters?.minFollowers)
            ?.value || null,
        followingTo:
          FOLLOWING_OPTIONS_MAX.find((f) => f.value === filters?.maxFollowers)
            ?.value || null,
        sports: filters?.sports || [],
        audienceGender: filters?.audienceGender || null,
        audienceAgeRanges: filters?.audienceAgeRanges || [],
        audienceEthnicities: filters?.audienceEthnicities || [],
        audienceLocations: filters?.audienceLocations || [],
        audienceInterests: filters?.audienceInterests || [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.id]);

  const handleSubmit = async () => {
    const {
      userRole,
      budget,
      flatFeePerStore,
      genders,
      maxAge,
      minAge,
      followingFrom,
      followingTo,
      sports,
      name,
      internalName,
      dueDate,
      guestName,
      company,
      email,
      guestBio,
      brandName,
      website,
      productBio,
      instagram,
      linkedin,
      otherSocial,
      instructions,
      physicalProductType,
      productName,
      productValue,
      usageRightsText,
      questions,
      applicationDeadlineDays,
      // applicationDeadlineEnabled,
      audienceGender,
      audienceAgeRanges,
      audienceEthnicities,
      audienceInterests,
      audienceLocations,
    } = form.getFieldsValue();

    const filesToUpload: File[] = [];
    const uploadUrls: string[] = [];
    const exampleFiles = form.getFieldValue('exampleFiles');
    exampleFiles?.forEach((file: any) => {
      if (file.key) {
        uploadUrls.push(file.key);
      } else if (file instanceof File) {
        filesToUpload.push(file);
      }
    });

    try {
      if (filesToUpload.length && !isGuest) {
        const { data: campaignExampleFiles } = await createCampaignExampleFiles(
          {
            variables: {
              input: {
                files: filesToUpload.map((file) => ({
                  name: file.name,
                })),
              },
            },
          }
        );
        if (campaignExampleFiles?.createCampaignExampleFiles?.length) {
          setIsUploading(true);
          const urls = await uploadImages(
            campaignExampleFiles.createCampaignExampleFiles,
            filesToUpload
          );
          setIsUploading(false);
          uploadUrls.push(...urls);
        }
      }

      const sponsoredAppearance: SponsoredAppearanceActivityDetailsInput = {
        target: isGuest
          ? CampaignActivityTargetType.SponsoredGuest
          : CampaignActivityTargetType.SponsoredProduct,
        videoType: isLive
          ? SponsoredAppearanceVideoType.Live
          : SponsoredAppearanceVideoType.Recorded,
        ...(isLive && { withAmplify: isAmplify }),
        ...(isGuest && {
          guest: {
            ...(guestBio && { bio: guestBio }),
            ...(company && { company }),
            ...(email && { email }),
            ...(instagram && { instagram }),
            ...(instructions && { instructions }),
            ...(linkedin && { linkedin }),
            ...(otherSocial && { otherSocial }),
            ...(guestName && { name: guestName }),
          },
        }),
        ...(!isGuest && {
          product: {
            ...(productBio && { bio: productBio }),
            ...(brandName && { name: brandName }),
            ...(website && { website }),
            ...(physicalProductType && { physicalProductType }),
            ...(productName && { productName }),
            ...(productValue && { productValue: Number(productValue) }),
            ...(instagram && { instagram }),
            ...(linkedin && { linkedin }),
            ...(otherSocial && { otherSocial }),
            ...(instructions && { instructions }),
            physicalProduct: isPhysicalProduct,
            ...(isPhysicalProduct && { productName }),
            ...(isPhysicalProduct && { productValue: Number(productValue) }),
            ...(uploadUrls?.length > 0 && {
              files: uploadUrls.map((url) => ({ key: url })),
            }),
          },
        }),
      };

      const activities: SponsoredAppearanceActivityInput[] = [
        {
          sponsoredAppearance,
          type: CampaignActivityType.SponsoredAppearance,
        },
      ];

      const audienceFilters = buildAudienceFilters({
        audienceGender,
        audienceAgeRanges,
        audienceEthnicities,
        audienceInterests,
        audienceLocations,
        isTikTok: false,
      });

      const criteria: SearchSponsoredAppearanceCriteriaInput = {
        appearanceTargetType: isGuest
          ? CampaignActivityTargetType.SponsoredGuest
          : CampaignActivityTargetType.SponsoredProduct,
        appearanceVideoType: isLive
          ? SponsoredAppearanceVideoType.Live
          : SponsoredAppearanceVideoType.Recorded,
        ...(userRole?.length && {
          roles: userRole?.length
            ? userRole
            : [UserRole.Athlete, UserRole.ContentCreator],
        }),
        ...(budget && { budget: Number(budget) }),
        ...(flatFeePerStore && { flatFeePerStore: Number(flatFeePerStore) }),
        ...(genders?.length && { genders }),
        ...(maxAge && {
          maxAge: Number(maxAge),
        }),
        ...(minAge && {
          minAge: Number(minAge),
        }),
        ...(followingFrom && {
          socialFollowersRange: {
            from: Number(followingFrom),
          },
        }),
        ...(followingTo && {
          socialFollowersRange: {
            to: Number(followingTo),
          },
        }),
        ...(followingTo &&
          followingFrom && {
            socialFollowersRange: {
              from: Number(followingFrom),
              to: Number(followingTo),
            },
          }),
        ...(sports?.length && { sports }),
        ...(audienceFilters && { audienceFilters }),
      };

      const updateCampaignInputVariables: UpdateSponsoredAppearanceInput = {
        lockEnabled: true,
        id: record?.id || '',
        criteria,
        ...(activities?.length && { activities }),
        ...(dueDate && { dueDate }),
        ...(usageRights && { usageRights }),
        ...(usageRightsText && { usageRightsText }),
        ...(questions?.length && {
          questions: questions?.map((item: string) => ({
            question: item,
            isRequired: true,
          })),
        }),
        ...(name && { name }),
        internalName: internalName || name,
        ...(applicationDeadlineDays && { applicationDeadlineDays }),
        // ...(applicationDeadlineEnabled !== undefined && {
        //   applicationDeadlineEnabled,
        // }),
      };

      const { data } = await updateSponsoredAppearanceCampaign({
        variables: {
          input: updateCampaignInputVariables,
        },
      });
      if (data?.updateSponsoredAppearanceCampaign) {
        successNotification('The campaign was successfully updated');
        onFinish();
      }
    } catch (error) {
      errorNotification((error as Error)?.message);
      console.error('Campaign update error:', { error });
    }
  };

  const handleChangeType = () => {
    setIsGuest(!isGuest);
  };

  const handleChangeVideoType = () => {
    setIsLive(!isLive);
    setIsAmplify(false);
  };

  const handleChangeAmplify = () => {
    setIsAmplify(!isAmplify);
  };

  // const handleSetImages = (incomingData: UploadImage[]) => {
  //   const newImages: ImageFiles[] = [];

  //   incomingData.forEach((image) => {
  //     newImages.push({
  //       key: image.data_key || '',
  //       url: image.data_url || null,
  //       file: image.file || undefined,
  //     });
  //   });

  //   setImageFiles(newImages);
  // };

  const handleIsPhysicalProductChange = () => {
    if (isPhysicalProduct) {
      form.setFieldsValue({
        physicalProductType: null,
      });
    }

    setIsPhysicalProduct(!isPhysicalProduct);
  };

  const handleUsageRightsChange = () => {
    if (usageRights) {
      form.setFieldsValue({
        usageRights: null,
      });
    }

    setUsageRights(!usageRights);
  };

  const handleFilterSearch = async (
    criteria: SearchSponsoredAppearanceCriteriaInput
  ) => {
    try {
      searchStores({
        variables: {
          input: {
            limit: 4,
            offset: 0,
            criteria,
          },
        },
      });
    } catch (error) {
      errorNotification((error as Error)?.message);
      console.error('Campaign filter error:', { error });
    }
  };

  const handleFilterClick = async () => {
    const {
      userRole,
      budget,
      flatFeePerStore,
      genders,
      maxAge,
      minAge,
      followingFrom,
      followingTo,
      sports,
      audienceGender,
      audienceAgeRanges,
      audienceEthnicities,
      audienceInterests,
      audienceLocations,
    } = form.getFieldsValue();

    const audienceFilters = buildAudienceFilters({
      audienceGender,
      audienceAgeRanges,
      audienceEthnicities,
      audienceInterests,
      audienceLocations,
      isTikTok: false,
    });

    const criteria: SearchSponsoredAppearanceCriteriaInput = {
      appearanceTargetType: isGuest
        ? CampaignActivityTargetType.SponsoredGuest
        : CampaignActivityTargetType.SponsoredProduct,
      appearanceVideoType: isLive
        ? SponsoredAppearanceVideoType.Live
        : SponsoredAppearanceVideoType.Recorded,
      ...(userRole?.length && {
        roles: userRole?.length
          ? userRole
          : [UserRole.Athlete, UserRole.ContentCreator],
      }),
      ...(budget && { budget: Number(budget) }),
      ...(flatFeePerStore && { flatFeePerStore: Number(flatFeePerStore) }),
      ...(genders?.length && { genders }),
      ...(maxAge && { maxAge: Number(maxAge) }),
      ...(minAge && { minAge: Number(minAge) }),
      ...(followingFrom && {
        socialFollowersRange: { from: Number(followingFrom) },
      }),
      ...(followingTo && {
        socialFollowersRange: { to: Number(followingTo) },
      }),
      ...(followingTo &&
        followingFrom && {
          socialFollowersRange: {
            from: Number(followingFrom),
            to: Number(followingTo),
          },
        }),
      ...(sports?.length && { sports }),
      ...(audienceFilters && { audienceFilters }), // Add audience filters
    };

    handleFilterSearch(criteria);
  };

  const talentsData =
    storeData?.searchSponsoredAppearanceCampaignStores?.entities;

  const hasMore =
    (talentsData?.length ?? 0) <
    (storeData?.searchSponsoredAppearanceCampaignStores.total ?? 0);

  const showTalentList =
    !!talentsData?.length && talentsData?.length > 0 && !loadingStoreData;

  const totalFollowers = Number(
    storeData?.searchSponsoredAppearanceCampaignStores.summary.followers
  );
  const totalTalents = Number(
    storeData?.searchSponsoredAppearanceCampaignStores.total
  );

  useEffect(() => {
    // Used to call the filters API and display the initial talents
    const {
      userRole,
      budget,
      flatFeePerStore,
      genders,
      maxAge,
      minAge,
      followingFrom,
      followingTo,
      sports,
      audienceGender,
      audienceAgeRanges,
      audienceEthnicities,
      audienceInterests,
      audienceLocations,
    } = initialFormValues;

    const audienceFilters = buildAudienceFilters({
      audienceGender,
      audienceAgeRanges,
      audienceEthnicities,
      audienceInterests,
      audienceLocations,
      isTikTok: false,
    });

    const criteria: SearchSponsoredAppearanceCriteriaInput = {
      appearanceTargetType: isGuest
        ? CampaignActivityTargetType.SponsoredGuest
        : CampaignActivityTargetType.SponsoredProduct,
      appearanceVideoType: isLive
        ? SponsoredAppearanceVideoType.Live
        : SponsoredAppearanceVideoType.Recorded,
      ...(userRole?.length && { roles: userRole }),
      ...(budget && { budget: Number(budget) }),
      ...(flatFeePerStore && { flatFeePerStore: Number(flatFeePerStore) }),
      ...(genders?.length && { genders }),
      ...(maxAge && {
        maxAge,
      }),
      ...(minAge && {
        minAge,
      }),
      ...(followingFrom && {
        socialFollowersRange: {
          from: Number(followingFrom),
        },
      }),
      ...(followingTo && {
        socialFollowersRange: {
          to: Number(followingTo),
        },
      }),
      ...(followingTo &&
        followingFrom && {
          socialFollowersRange: {
            from: Number(followingFrom),
            to: Number(followingTo),
          },
        }),
      sports,
      ...(audienceFilters && { audienceFilters }),
    };

    handleFilterSearch(criteria);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitLoading =
    isUploading ||
    loadingUpdateCampaign ||
    loadingCreateCampaignExampleFiles ||
    loadingStoreData;

  if (!record) return null;

  return (
    <div className={styles.root}>
      <Form
        name="EditCampaignForm"
        aria-label="Update Sponsored Appearance Campaign"
        layout="vertical"
        onFinish={handleSubmit}
        form={form}
        initialValues={initialFormValues}
      >
        <div className={styles.columnWrapper}>
          <div className={styles.switchWrapper}>
            <Text>Types of Stream & Podcast Campaign</Text>

            <Switch
              checked={isGuest}
              checkedChildren="Guest"
              unCheckedChildren="Product"
              onChange={handleChangeType}
            />
          </div>

          <div className={styles.switchWrapper}>
            <Text>Video Type</Text>

            <Switch
              checked={isLive}
              checkedChildren="Live"
              unCheckedChildren="Recorded"
              onChange={handleChangeVideoType}
            />
          </div>

          {isLive && (
            <div className={styles.switchWrapper}>
              <Text>AMPLIFY to socials</Text>
              <Checkbox
                value={isAmplify}
                onChange={handleChangeAmplify}
                checked={isAmplify}
              />
            </div>
          )}
        </div>

        <CampaignCriteria
          record={record}
          form={form}
          initialFormValues={initialFormValues}
          isInstagram={false} // Not used for sponsored appearance
          setIsInstagram={() => {
            /* Sponsored appearance doesn't use Instagram */
          }}
          isTwitter={false} // Not used for sponsored appearance
          setIsTwitter={() => {
            /* Sponsored appearance doesn't use Twitter */
          }}
          setSelectedTasks={() => {
            /* Sponsored appearance doesn't use tasks */
          }}
          deliverablesChecklist={[]} // Not used for sponsored appearance
          setDeliverablesChecklist={() => {
            /* Sponsored appearance doesn't use deliverables */
          }}
          handleFilterClick={handleFilterClick}
          loadingStoreData={loadingStoreData}
          talentsData={talentsData}
          hasMore={hasMore}
          totalTalents={totalTalents}
          totalFollowers={totalFollowers}
          showTalentList={showTalentList}
          showPlatformSelection={false} // Sponsored appearance doesn't use platform selection
          showLocationSelection={false} // Sponsored appearance doesn't use location selection
          showHashtagSelection={false} // Sponsored appearance doesn't use hashtag selection
          showEngagementRate={false} // Sponsored appearance doesn't use engagement rate
          canEditPremiumFilters={canEditPremiumFiltersValue}
        />

        <CampaignDetails
          record={record}
          showInstructions={false}
          renderCustomFields={() => (
            <>
              <div className={styles.rowWrapper}>
                <Form.Item
                  name="usageRights"
                  label={<Text>Usage Rights</Text>}
                  valuePropName="checked"
                  initialValue={usageRights}
                >
                  <Checkbox
                    value={usageRights}
                    onChange={handleUsageRightsChange}
                  />
                </Form.Item>

                <Form.Item
                  name="usageRightsText"
                  rules={[
                    {
                      required: usageRights,
                      message: 'This field is required',
                    },
                  ]}
                  label={<Text>Usage rights text</Text>}
                >
                  <TextArea
                    rows={6}
                    id="usageRights"
                    placeholder="Usage rights text"
                    aria-label="Usage rights text"
                  />
                </Form.Item>
              </div>
            </>
          )}
        />

        <div className={styles.heading}>Talent Questions:</div>

        <TalentQuestions
          name="questions"
          placeholder="Type a question…"
          editable
          maxQuestions={5}
        />

        {isGuest ? (
          <>
            <div className={styles.heading}>Guest Details</div>

            <div className={styles.threeItemsRowWrapper}>
              <Form.Item
                name="guestName"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>Guest Name</Text>}
              >
                <Input
                  type="guestName"
                  placeholder="Enter Guest name"
                  aria-label="Guest name"
                />
              </Form.Item>

              <Form.Item name="company" label={<Text>Company (Optional)</Text>}>
                <Input
                  type="company"
                  placeholder="Enter Company name"
                  aria-label="Company name"
                />
              </Form.Item>

              <Form.Item
                name="email"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                  {
                    type: 'email',
                    message: 'Please input a valid email address',
                  },
                ]}
                label={<Text>Guest E-Mail</Text>}
              >
                <Input
                  type="email"
                  placeholder="Enter Guest email"
                  aria-label="Guest email"
                />
              </Form.Item>
            </div>
            <div>
              <Form.Item
                name="guestBio"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>Bio</Text>}
              >
                <TextArea
                  autoSize={{ minRows: 3 }}
                  name="guestBio"
                  placeholder="Please give the host some background on you and your product/service/company to decide if its a good fit for the show/stream."
                />
              </Form.Item>
            </div>
          </>
        ) : (
          <>
            <div className={styles.heading}>Brand info</div>

            <div className={styles.rowWrapper}>
              <Form.Item
                name="brandName"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>{"Brand's name"}</Text>}
              >
                <Input
                  type="brandName"
                  placeholder="Enter Brand's name"
                  aria-label="Brand name"
                />
              </Form.Item>
              <Form.Item name="website" label={<Text>Website</Text>}>
                <Input
                  type="website"
                  placeholder="Website"
                  aria-label="Website"
                />
              </Form.Item>
            </div>
            <div>
              <Form.Item
                name="productBio"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>Bio</Text>}
              >
                <TextArea
                  autoSize={{ minRows: 3 }}
                  name="productBio"
                  placeholder="Please give the host some background on you and your product/service/company to decide if its a good fit for the show/stream."
                />
              </Form.Item>
            </div>
          </>
        )}

        <div className={styles.heading}>Social Media Accounts</div>

        <div className={styles.threeItemsRowWrapper}>
          <Form.Item name="instagram" label={<Text>Instagram</Text>}>
            <Input
              type="instagram"
              placeholder="Enter Instagram"
              aria-label="Instagram"
            />
          </Form.Item>

          <Form.Item name="linkedin" label={<Text>LinkedIn</Text>}>
            <Input
              type="linkedin"
              placeholder="Enter LinkedIn"
              aria-label="LinkedIn"
            />
          </Form.Item>
          <Form.Item name="otherSocial" label={<Text>Other</Text>}>
            <Input
              type="otherSocial"
              placeholder="Enter Other"
              aria-label="Other"
            />
          </Form.Item>
        </div>

        {isGuest ? (
          <Form.Item
            name="instructions"
            rules={[
              {
                required: true,
                message: 'This field is required',
              },
            ]}
            label={<Text>Instructions</Text>}
          >
            <TextArea
              autoSize={{ minRows: 3 }}
              name="instructions"
              placeholder="Enter instructions"
            />
          </Form.Item>
        ) : (
          <>
            <div className={styles.heading}>Product Info and Instructions</div>

            <div className={styles.rowWrapper}>
              <Form.Item
                name="physicalProduct"
                label={<Text>Yes, I need to send them physical products</Text>}
                valuePropName="checked"
                initialValue={isPhysicalProduct}
              >
                <Checkbox
                  value={isPhysicalProduct}
                  onChange={handleIsPhysicalProductChange}
                />
              </Form.Item>

              <Form.Item
                name="physicalProductType"
                rules={[
                  {
                    required: isPhysicalProduct,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>Product Shipping Category</Text>}
              >
                <Select
                  id="physicalProductType"
                  placeholder="Select"
                  disabled={!isPhysicalProduct}
                  filterOption={(inputValue, option) =>
                    option?.props.children
                      .toString()
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  }
                >
                  {PRODUCT_TYPES.map((item) => {
                    return (
                      <Option value={`${item.value}`} key={item.label}>
                        {item.label}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </div>

            {isPhysicalProduct && (
              <div className={styles.rowWrapper}>
                <Form.Item
                  name="productName"
                  rules={
                    isPhysicalProduct
                      ? [
                          {
                            required: true,
                            message: 'This field is required',
                          },
                        ]
                      : []
                  }
                  label={<Text>Product Name</Text>}
                >
                  <Input
                    type="text"
                    placeholder="Enter Product Name"
                    aria-label="Product Name"
                  />
                </Form.Item>

                <Form.Item
                  name="productValue"
                  rules={
                    isPhysicalProduct
                      ? [
                          {
                            required: true,
                            message: 'This field is required',
                          },
                        ]
                      : []
                  }
                  label={<Text>Product Value</Text>}
                >
                  <Input
                    type="number"
                    placeholder="Enter Product Value"
                    aria-label="Product Value"
                  />
                </Form.Item>
              </div>
            )}

            <div>
              <Form.Item
                name="instructions"
                label={<Text>Campaign instructions</Text>}
              >
                <TextArea
                  autoSize={{ minRows: 3 }}
                  name="instructions"
                  placeholder="Enter instructions"
                />
              </Form.Item>
            </div>

            <div className={styles.examplesHeading}>Examples and Uploads</div>

            <Form.Item getValueFromEvent={normFile} shouldUpdate>
              {(form) => {
                return (
                  <UploadFilesInput
                    title="Upload Example Files"
                    value={form.getFieldValue('exampleFiles') || []}
                    onChange={(newValue) =>
                      form.setFieldsValue({ exampleFiles: newValue })
                    }
                    acceptFileTypes={[
                      'image/png',
                      'image/jpeg',
                      'application/pdf',
                      'video/mp4',
                      'video/mov',
                      'video/avi',
                      'video/wmv',
                      'video/flv',
                      'video/mpeg',
                      'video/mpg',
                    ]}
                  />
                );
              }}
            </Form.Item>
          </>
        )}
        <div className={styles.submitButtonWrapper}>
          <Button
            style={{ marginRight: 8 }}
            type="primary"
            loading={submitLoading}
            onClick={handleSubmit}
          >
            Save as Draft
          </Button>

          <Button type="primary" htmlType="submit" loading={submitLoading}>
            Submit
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default SponsoredAppearanceCampaignEditModal;
